import type { Order } from '@commercelayer/sdk'
import z from 'zod'

/**
 * Represents an Algolia product object inferred from a schema.
 * @typedef {Object} AlgoliaObject
 */
type AlgoliaObject = z.infer<typeof algoliaProductsSchema>

/**
 * Payload for cart-related Google Analytics events.
 * @typedef {Object} CartEventsPayload
 * @property {Order} [cart] - The current cart order.
 * @property {Array<AlgoliaObject | null>} [algoliaObjects] - Array of Algolia product objects.
 */
type CartEventsPayload = {
  cart?: Order
  algoliaObjects?: Array<AlgoliaObject | null>
}

/**
 * Composable for managing Google Analytics ecommerce data.
 * Provides methods to retrieve and set cart events payload for analytics tracking.
 *
 * @returns {Object} An object with cart events payload management methods
 * @property {Ref<CartEventsPayload | null | undefined>} cartEventsPayload - Reactive cart events payload
 * @property {Function} getEcommerceData - Retrieves ecommerce data for analytics
 * @property {Function} setCartEventsPayload - Sets or updates cart events payload
 */
export const useGAEcommerceData = () => {
  const { mapData } = useMapGAEcommerceData()
  const cartEventsPayload = useGAEntity<CartEventsPayload | null | undefined>(
    'CART_GA_EVENTS_PAYLOAD'
  )

  /**
   * Retrieves ecommerce data for Google Analytics tracking.
   *
   * @async
   * @param {boolean} [actionFromChat] - Indicates if the action originated from a chat interaction
   * @param {number} [qty] - Optional quantity parameter
   * @returns {Promise<Object | undefined>} Mapped ecommerce data or undefined if no cart exists
   */
  const getEcommerceData = async (actionFromChat?: boolean, qty?: number) => {
    const cart = cartEventsPayload.value?.cart
    if (!cart || !cart?.line_items?.length) return
    return await mapData({
      cart,
      algoliaObjects: cartEventsPayload.value?.algoliaObjects ?? [],
      actionFromChat,
      qty,
    })
  }

  /**
   * Sets or updates the cart events payload for Google Analytics.
   *
   * @param {CartEventsPayload} payload - The payload to set or update
   */
  const setCartEventsPayload = (payload: CartEventsPayload) => {
    cartEventsPayload.value = {
      ...cartEventsPayload.value,
      ...payload,
    }
  }

  return { cartEventsPayload, getEcommerceData, setCartEventsPayload }
}
